<template>
    <div class="question-component">
        <h3>{{ question.question }}</h3>
        <div class="mb-2" v-if="
                question.is_markable &&
                question.question_type.name !== 'mamc' &&
                question.question_type.name !== 'samc'
            ">
            <small class="text-muted">Up to {{ question.marks }} {{ question.marks === 1 ? 'mark' : 'marks' }} available.</small>
        </div>

        <p v-html="question.descriptionMD" v-if="question.description"></p>

        <template v-if="question.question_type.name === 'amc' || question.question_type.name === 'aft'">
            <div class="my-1">
                <audio :src="question.audio_file_url" controls></audio>
            </div>
        </template>


        <div class="mb-2" v-if="question.is_optional || question.question_type.name === 'samc' || question.question_type.name === 'mamc'">
            <span v-if="question.is_optional"><small class="text-muted"><strong>Optional.</strong></small></span>
            <span v-if="question.question_type.name === 'samc'">
                <small class="text-muted" v-if="question.is_markable">Up to {{ question.marks }} {{ question.marks === 1 ? 'mark' : 'marks' }} available.</small>
            </span>
            <span v-if="question.question_type.name === 'mamc'">
                <small class="text-muted">Choose all correct options to receive {{ question.marks }} {{ question.marks === 1 ? 'mark' : 'marks' }}.</small>
            </span>
        </div>

        <!-- Multiple-choice (single answer) -->
        <div v-if="question.question_type.name === 'samc'" class="form-group samc mt-2">
            <div v-for="answer in question.answers" :key="answer.id" class="custom-control custom-radio">
                <input
                    type="radio"
                    :name="'question-' + question.id"
                    :id="'a-'+answer.id"
                    :value="answer.id"
                    @change="emitAnswer(answer.id)"
                    class="custom-control-input"
                >
                <label class="custom-control-label" :for="'a-'+answer.id">
                    {{ answer.answer }}
                </label>
            </div>
        </div>

        <!-- Multiple-choice (multiple answers) -->
        <div v-else-if="question.question_type.name === 'mamc'" class="form-group mamc mt-2">
            <div v-for="answer in question.answers" :key="answer.id" class="custom-control custom-checkbox">
                <input
                    type="checkbox"
                    :name="'question-' + question.id"
                    :id="'a-'+answer.id"
                    :value="answer.id"
                    @change="emitAnswer"
                    class="custom-control-input"
                >
                <label class="custom-control-label" :for="'a-'+answer.id">
                    {{ answer.answer }}
                </label>
            </div>
        </div>

        <!-- Free text -->
        <div v-else-if="question.question_type.name === 'text' || question.question_type.name === 'aft'" class="form-group text">
            <textarea
                :name="'question-' + question.id"
                @input="emitAnswer($event.target.value)"
                class="form-control"
                :maxlength="question.allowed_length"
            ></textarea>
            <small class="text-muted">Max {{ question.allowed_length }} characters.</small>
        </div>

        <!-- Audio multiple-choice (single answer) -->
        <div v-else-if="question.question_type.name === 'amc'" class="form-group amc mt-2">
            <div v-for="answer in question.answers" :key="answer.id" class="custom-control custom-radio">
                <input
                    type="radio"
                    :name="'question-' + question.id"
                    :id="'a-'+answer.id"
                    :value="answer.id"
                    @change="emitAnswer(answer.id)"
                    class="custom-control-input"
                >
                <label class="custom-control-label" :for="'a-'+answer.id">
                    {{ answer.answer }}
                </label>
            </div>
        </div>

        <!-- Audio upload -->
        <div v-else-if="question.question_type.name === 'audio'" class="form-group audio mt-2">
            <input type="file" @change="(e) => emitAnswer(e.target.files[0], question.id, 'audio')" class="form-control-file" accept=".mp3" required>
            <div class="mb-2"><small class="text-muted">Upload an mp3 file.</small></div>

            <div class="form-group">
                <!--<label>Please give some detail around your uploaded music file</label>-->
                <textarea
                    :name="'audio-text-' + question.id"
                    @input="updateAudioText($event.target.value, question.id)"
                    class="form-control"
                    :maxlength="question.allowed_length"
                ></textarea>
                <small class="text-muted">Max {{ question.allowed_length }} characters</small>
            </div>
        </div>

        <!-- Video upload -->
        <div v-else-if="question.question_type.name === 'video'" class="form-group video mt-2">
            <exam-mux-video-upload-component
                @video-uploaded="(data) => updateUploadedAnswer(question.id, data)"
            ></exam-mux-video-upload-component>
            <input
                type="hidden"
                :name="'video-id-' + question.id"
                :ref="'videoId' + question.id"
            >
            <div class="mb-2"><small class="text-muted">Upload an mp4, avi or mov file.</small></div>

            <div class="form-group">
                <label>Please give some detail around your uploaded video file</label>
                <textarea
                    :name="'video-text-' + question.id"
                    @input="updateVideoText($event.target.value, question.id)"
                    class="form-control"
                    :maxlength="question.allowed_length"
                ></textarea>
                <small class="text-muted">Max {{ question.allowed_length }} characters</small>
            </div>
        </div>
    </div>
</template>

<style scoped>
.question-component {
    margin-bottom: 2rem;
}
.form-group {
    margin-bottom: 0;
}
</style>

<script>
export default {
    props: {
        question: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            videoAnswers: {}, // Store video ID and text for each video question
            audioAnswers: {}, // Store audio file and text for each video question
        };
    },

    methods: {
        emitAnswer(value, questionId, type) {
            if (type === 'audio') {
                // Update audioAnswers for the current question
                this.$set(this.audioAnswers, questionId, {
                    ...this.audioAnswers[questionId],
                    file: value,
                });

                // Emit the answer if both file and text are present
                this.emitAudioAnswer(questionId);
            } else if (this.question.question_type.name === 'mamc') {
                // For multiple-choice, accumulate answers
                let selected = Array.from(document.querySelectorAll(`input[name='question-${this.question.id}']:checked`), input => input.value);
                this.$emit('answer-selected', this.question.id, selected);
            } else {
                // For single-choice or text, emit answer directly
                this.$emit('answer-selected', this.question.id, value);
            }
        },

        // Update the hidden input and videoAnswers for the current question
        updateUploadedAnswer(questionId, data) {
            // Update the hidden input for the question ID
            this.$refs['videoId' + questionId].value = data.id;

            // Update videoAnswers for the current question
            this.$set(this.videoAnswers, questionId, {
                ...this.videoAnswers[questionId],
                id: data.id,
            });

            // Emit the answer if both id and text are present
            this.emitVideoAnswer(questionId);
        },

        // Update the videoAnswers for the current question
        updateVideoText(value, questionId) {
            // Update videoAnswers for the current question
            this.$set(this.videoAnswers, questionId, {
                ...this.videoAnswers[questionId],
                text: value,
            });

            // Emit the answer if both id and text are present
            this.emitVideoAnswer(questionId);
        },

        // Emit the answer if both id and text are present
        emitVideoAnswer(questionId) {
            const { id, text } = this.videoAnswers[questionId];
            if (id && text) {
                this.$emit('answer-selected', questionId, { id, text });
            }
        },

        // Update the audioAnswers for the current question
        updateAudioText(value, questionId) {
            // Update audioAnswers for the current question
            this.$set(this.audioAnswers, questionId, {
                ...this.audioAnswers[questionId],
                text: value,
            });

            // Emit the answer if both file and text are present
            this.emitAudioAnswer(questionId);
        },

        // Emit the answer if both file and text are present
        emitAudioAnswer(questionId) {
            const { file, text } = this.audioAnswers[questionId];
            if (file && text) {
                this.$emit('answer-selected', questionId, { file, text });
            }
        },
    },
};
</script>
